import { AutomationFlowAnimation } from '@/components/marketing/v2/landing/animations/automation-flow-animation'
import { FirstBentoAnimation } from '@/components/marketing/v2/landing/animations/first-bento-animation'
import { SecondBentoAnimation } from '@/components/marketing/v2/landing/animations/second-bento-animation'
import { SegmentationBentoAnimation } from '@/components/marketing/v2/landing/animations/segmentation-bento-animation'
import { MaskedLogo } from '@/components/marketing/v2/masked-logo'
import { USD_PER_CREDIT } from '@/lib/billing/credit-economics'
import {
  BILLING_PLANS,
  type BillingPlanKey,
  PURCHASABLE_PLAN_KEYS,
} from '@/lib/billing/plans'
import { formatCurrency } from '@/lib/billing/price-summary'
import { BREW_DEMO_BOOKING_URL } from '@/lib/site/booking'

import { Highlight } from './highlight'

/**
 * Pretty-print a plan capacity number for the pricing comparison
 * table. `Number.MAX_SAFE_INTEGER` is treated as "Unlimited" so the
 * Enterprise column reads naturally.
 */
function formatPlanNumber(value: number): string {
  if (value === Number.MAX_SAFE_INTEGER) {
    return 'Unlimited'
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  }
  return value.toLocaleString('en-US')
}

/**
 * Build one row of the comparison-table `Usage` section by reading
 * the per-plan capacity from `BILLING_PLANS`. Keeps the comparison
 * table aligned with the canonical billing source — the pricing
 * cards above the table also derive their bullets from
 * `BILLING_PLANS`, so a single edit there flows to both surfaces.
 *
 * Enterprise marketing copy depends on the field:
 *   - Credits / Sends: zero in `BILLING_PLANS` (sales-only) → "Custom"
 *   - Domains / Brands: `Number.MAX_SAFE_INTEGER` → "Unlimited"
 * Pass an explicit `enterpriseFallback` to control which is used
 * when the raw value would otherwise read as `0`.
 */
/**
 * The comparison table's columns, left to right. Sunset tiers are excluded —
 * they aren't on sale, so a marketing comparison must not offer them.
 *
 * EVERY row is projected through this one array (see `buildUsageRow` and
 * `buildFlagRow`). Do not hand-write positional value tuples: the column set
 * is derived, so a literal `['check', 'check', …]` silently misaligns the
 * moment a plan is added or sunset.
 */
const COMPARISON_PLAN_KEYS = PURCHASABLE_PLAN_KEYS

function buildUsageRow(
  label: string,
  pick: (plan: (typeof BILLING_PLANS)[keyof typeof BILLING_PLANS]) => number,
  enterpriseFallback: 'Custom' | 'Unlimited' = 'Custom'
): { name: string; values: Array<string> } {
  return {
    name: label,
    values: COMPARISON_PLAN_KEYS.map((key) => {
      const value = pick(BILLING_PLANS[key])
      if (key === 'enterprise' && value === 0) {
        return enterpriseFallback
      }
      return formatPlanNumber(value)
    }),
  }
}

/**
 * A non-capacity row, keyed BY PLAN rather than by column position.
 * `fallback` covers every plan not named in `byPlan`.
 */
function buildFlagRow(
  label: string,
  byPlan: Partial<Record<BillingPlanKey, string>> = {},
  fallback = 'check'
): { name: string; values: Array<string> } {
  return {
    name: label,
    values: COMPARISON_PLAN_KEYS.map((key) => byPlan[key] ?? fallback),
  }
}

export const marketingContent = {
  features: [
    {
      name: 'AI Email Generation',
      description: 'Create complete email campaigns from simple prompts',
    },
    {
      name: 'Brand Consistency',
      description:
        'Automatic extraction and application of brand colors, logos, and fonts',
    },
    {
      name: 'Email Automations',
      description:
        'Build sophisticated sequences with a single prompt (welcome, onboarding, re-engagement)',
    },
    {
      name: 'Remix Gallery',
      description: 'Browse and customize AI-generated email templates',
    },
    {
      name: 'Analytics',
      description: 'Real-time performance tracking and AI-powered insights',
    },
    {
      name: 'Deliverability',
      description: 'Built-in DKIM, SPF, DMARC authentication',
    },
  ],
  useCases: [
    'Marketing Campaigns',
    'Welcome Email Sequences',
    'Onboarding Flows',
    'Post-Purchase Follow-ups',
    'Re-engagement Campaigns',
    'Newsletter Creation',
    'Transactional Emails',
    'Abandoned Cart Reminders',
    'Birthday and Anniversary Emails',
  ],
  keywords: [
    'AI Email Marketing',
    'Email Marketing',
    'Email Marketing Platform',
    'Email Marketing Automation',
  ],
  links: {
    email: 'support@brew.new',
    twitter: 'https://x.com/brewdotnew',
    youtube: 'https://www.youtube.com/@brewdotnew',
    linkedin: 'https://www.linkedin.com/company/brewdotnew',
    slack:
      'https://join.slack.com/t/brew-community/shared_invite/zt-40dehsmus-twEChn6F5YCzcB_ep4g1nQ',
    /**
     * Public Brew status page. Currently held behind the redaction
     * placeholder; consumers (e.g. the marketing footer status pill)
     * detect the literal `[REDACTED]` token and render the badge as a
     * non-link element so the browser does not throw a URL parse
     * error on every page load.
     */
    statusPage: 'https://brew.statuspage.[REDACTED]/',
  },
  nav: {
    links: [
      { id: 1, name: 'Remix', href: '#remix' },
      { id: 2, name: 'Docs', href: '#docs' },
      { id: 3, name: 'Pricing', href: '#pricing' },
    ],
  },
  hero: {
    badgeIcon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="fill-[#364153] dark:fill-white"
      >
        <path d="M7.62758 1.09876C7.74088 1.03404 7.8691 1 7.99958 1C8.13006 1 8.25828 1.03404 8.37158 1.09876L13.6216 4.09876C13.7363 4.16438 13.8316 4.25915 13.8979 4.37347C13.9642 4.48779 13.9992 4.6176 13.9992 4.74976C13.9992 4.88191 13.9642 5.01172 13.8979 5.12604C13.8316 5.24036 13.7363 5.33513 13.6216 5.40076L8.37158 8.40076C8.25828 8.46548 8.13006 8.49952 7.99958 8.49952C7.8691 8.49952 7.74088 8.46548 7.62758 8.40076L2.37758 5.40076C2.26287 5.33513 2.16753 5.24036 2.10123 5.12604C2.03492 5.01172 2 4.88191 2 4.74976C2 4.6176 2.03492 4.48779 2.10123 4.37347C2.16753 4.25915 2.26287 4.16438 2.37758 4.09876L7.62758 1.09876Z" />
        <path d="M2.56958 7.23928L2.37758 7.34928C2.26287 7.41491 2.16753 7.50968 2.10123 7.624C2.03492 7.73831 2 7.86813 2 8.00028C2 8.13244 2.03492 8.26225 2.10123 8.37657C2.16753 8.49089 2.26287 8.58566 2.37758 8.65128L7.62758 11.6513C7.74088 11.716 7.8691 11.75 7.99958 11.75C8.13006 11.75 8.25828 11.716 8.37158 11.6513L13.6216 8.65128C13.7365 8.58573 13.8321 8.49093 13.8986 8.3765C13.965 8.26208 14 8.13211 14 7.99978C14 7.86745 13.965 7.73748 13.8986 7.62306C13.8321 7.50864 13.7365 7.41384 13.6216 7.34828L13.4296 7.23828L9.11558 9.70328C8.77568 9.89744 8.39102 9.99956 7.99958 9.99956C7.60814 9.99956 7.22347 9.89744 6.88358 9.70328L2.56958 7.23928Z" />
        <path d="M2.37845 10.5993L2.57045 10.4893L6.88445 12.9533C7.22435 13.1474 7.60901 13.2496 8.00045 13.2496C8.39189 13.2496 8.77656 13.1474 9.11645 12.9533L13.4305 10.4883L13.6225 10.5983C13.7374 10.6638 13.833 10.7586 13.8994 10.8731C13.9659 10.9875 14.0009 11.1175 14.0009 11.2498C14.0009 11.3821 13.9659 11.5121 13.8994 11.6265C13.833 11.7409 13.7374 11.8357 13.6225 11.9013L8.37245 14.9013C8.25915 14.966 8.13093 15 8.00045 15C7.86997 15 7.74175 14.966 7.62845 14.9013L2.37845 11.9013C2.2635 11.8357 2.16795 11.7409 2.10148 11.6265C2.03501 11.5121 2 11.3821 2 11.2498C2 11.1175 2.03501 10.9875 2.10148 10.8731C2.16795 10.7586 2.2635 10.6638 2.37845 10.5983V10.5993Z" />
      </svg>
    ),
    badge: 'Introducing custom automations',
    title: 'What emails can I help you Brew?',
    description: 'Lovable for Email Marketing',
    cta: {
      primary: {
        text: 'Get Started',
        href: '#',
      },
      secondary: {
        text: 'Log In',
        href: '#',
      },
    },
  },
  companyShowcase: {
    companyLogos: [
      {
        id: 1,
        name: 'Public.com',
        logo: (
          <MaskedLogo
            src="/logo/Public.png"
            alt="Public.com"
            width={120}
            height={23}
          />
        ),
      },
      {
        id: 2,
        name: 'Kittl',
        logo: (
          <MaskedLogo
            src="/logo/Kittl.svg"
            alt="Kittl"
            width={75}
            height={18}
          />
        ),
      },
      {
        id: 3,
        name: 'Harmonya',
        logo: (
          <MaskedLogo
            src="/logo/Harmonya.png"
            alt="Harmonya"
            width={140}
            height={28}
          />
        ),
      },
      {
        id: 4,
        name: 'Live Flow',
        logo: (
          <MaskedLogo
            src="/logo/LiveFlow.svg"
            alt="Live Flow"
            width={100}
            height={31}
          />
        ),
      },
      {
        id: 5,
        name: 'Tailwind',
        logo: (
          <MaskedLogo
            src="/logo/tailwind.svg"
            alt="Tailwind"
            width={140}
            height={46}
          />
        ),
      },
    ],
  },
  bentoSection: {
    title: 'Lovable for Email Marketing',
    description:
      'Create and send on-brand email campaigns and automations in seconds with AI.',
    items: [
      {
        id: 1,
        content: <FirstBentoAnimation isHovered />,
        title: 'Create Emails with AI',
        background: 'waves2.png',
        description:
          'Describe what you want in plain English and get polished, on-brand emails in seconds.',
      },
      {
        id: 2,
        content: <SecondBentoAnimation isHovered />,
        background: 'waves2.png',
        title: 'Edit with AI, Like in Lovable',
        description:
          'Edit with natural language prompts like you would in Lovable or use the intuitive editor for pixel-perfect control.',
      },
      {
        id: 3,
        content: <AutomationFlowAnimation isHovered />,
        background: 'waves2.png',
        title: 'Build Complex Automations with AI',
        description:
          'Create sophisticated email sequences with branching logic and personalization through simple prompts.',
      },
      {
        id: 4,
        content: <SegmentationBentoAnimation isHovered />,
        background: 'waves2.png',
        title: 'Segment with Simple Language',
        description:
          'Just describe who you want to reach and Brew builds the segment for you.',
      },
      {
        id: 5,
        content: (
          <div className="absolute inset-0 h-full w-full overflow-hidden">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 h-full w-full object-cover opacity-90"
            >
              <source
                src="https://cdn.brew.new/videos/send-drive-conversion.mp4"
                type="video/mp4"
              />
            </video>
          </div>
        ),
        background: 'waves2.png',
        title: 'Send All Email Types',
        description:
          'Create and send campaigns, automated sequences, and transactional emails with consistent branding.',
      },
    ],
  },
  pricing: {
    title: "We've got a plan that's perfect for you",
    description:
      'Every plan includes all features. The only differences are volume, credits, and the watermark.',
    // The plan cards themselves render from BILLING_PLANS via the PlanGrid /
    // markdown catalog — never from hand-written copies here. (A dead
    // placeholder list with a $12 "Startup" tier used to live at this key and
    // was one future consumer away from shipping fiction to the pricing page.)
    comparisonTable: {
      plans: COMPARISON_PLAN_KEYS.map((key) => BILLING_PLANS[key].name),
      sections: [
        {
          name: 'Usage',
          // Capacity rows are derived from `BILLING_PLANS` so the
          // table can never drift from the pricing cards above it.
          // The `AI models` row is hand-curated marketing copy that
          // does not live on `BILLING_PLANS`.
          features: [
            buildUsageRow('AI credits / month', (p) => p.credits),
            buildUsageRow('Email sends / month', (p) => p.emailSends),
            buildUsageRow('Sending domains', (p) => p.domains),
            buildUsageRow('Brand workspaces', (p) => p.brands),
            buildFlagRow('AI models', { free: 'Basic' }, 'Latest'),
          ],
        },
        {
          name: 'AI Features',
          features: [
            buildFlagRow('On-brand emails'),
            buildFlagRow('Custom image styles'),
            buildFlagRow('Email remix'),
            buildFlagRow('Recommendations'),
          ],
        },
        {
          name: 'Email Types',
          features: [
            buildFlagRow('Campaign emails'),
            buildFlagRow('Automation emails'),
            buildFlagRow('Transaction emails'),
          ],
        },
        {
          name: 'Platform',
          features: [
            buildFlagRow('Unlimited integrations'),
            buildFlagRow('Brew watermark removed', { free: 'dash' }),
            buildFlagRow('Priority support', { free: 'dash' }),
            buildFlagRow(
              'Dedicated account manager',
              { enterprise: 'check' },
              'dash'
            ),
            buildFlagRow('Migration support', { enterprise: 'check' }, 'dash'),
          ],
        },
      ],
    },
  },
  testimonials: [
    {
      id: '1',
      name: 'Eissra Eltom',
      company: 'The Browser Company',
      img: '/people/eissra_eltom.jpeg',
      companyImg: '/logo/thebrowser.png',
      description: (
        <p>
          It&apos;s
          <Highlight>like Cursor, but for email marketing. Genius!</Highlight>{' '}
          The AI-powered workflow has completely transformed how we approach our
          campaigns.
        </p>
      ),
    },
    {
      id: '2',
      name: 'Walker Nordin',
      company: 'Tailwind',
      img: '/people/walker_nordin.jpeg',
      companyImg: '/logo/tailwindcss.png',
      description: (
        <p>
          Brew&apos;s AI has revolutionized how we approach email campaigns.
          <Highlight>
            Our engagement rates have skyrocketed, and it&apos;s saved us
            countless hours.
          </Highlight>{' '}
          The best investment we&apos;ve made in our marketing stack.
        </p>
      ),
    },
    {
      id: '3',
      name: 'Lasse Kalkar',
      company: 'LiveFlow',
      img: '/people/lasse_kalkar.jpeg',
      companyImg: '/logo/liveflow.png',
      description: (
        <p>
          I use Cursor for coding, v0 for frontend, Lovable for websites and now
          Brew for emails!
          <Highlight>The AI-powered workflow is a game-changer.</Highlight>{' '}
          It&apos;s the missing piece in my productivity stack.
        </p>
      ),
    },
    {
      id: '4',
      name: 'Thomas Park',
      company: 'Vercel',
      img: '/people/thomas_park.jpeg',
      companyImg: '/logo/vercel.png',
      description: (
        <p>
          The ability to
          <Highlight>
            maintain brand consistency across all emails with Brew&apos;s design
            system and voice profiles is incredible.
          </Highlight>{' '}
          Highly recommended for any team serious about email marketing!
        </p>
      ),
    },
    {
      id: '5',
      name: 'Ohans Emmanuel',
      company: 'HelloFresh',
      img: '/people/ohans_emmanuel.jpeg',
      companyImg: '/logo/hellofresh.png',
      description: (
        <p>
          We were struggling to scale our email efforts.
          <Highlight>
            Brew&apos;s AI not only writes better copy but also helps us analyze
            performance and iterate faster.
          </Highlight>{' '}
          It&apos;s been transformative for our team.
        </p>
      ),
    },
    {
      id: '6',
      name: 'Zach El-Tayyeb',
      company: 'Nova Credit',
      img: '/people/zach_el-Tayyeb.jpeg',
      companyImg: '/logo/novacredit.png',
      description: (
        <p>
          As a small team, Brew is like having an expert email marketer on
          staff.
          <Highlight>The AI recommendations are spot on.</Highlight> We&apos;ve
          seen immediate improvements in our email metrics.
        </p>
      ),
    },
    {
      id: '7',
      name: 'Eric Moch',
      company: 'Glimpse',
      img: '/people/eric_moch.jpeg',
      companyImg: '/logo/meetglimpse.png',
      description: (
        <p>
          The AI-generated email designs are
          <Highlight>stunning and always on-brand.</Highlight> It&apos;s saved
          us so much time compared to manual design. Our design team can now
          focus on strategy rather than execution.
        </p>
      ),
    },
    {
      id: '8',
      name: 'Zach Garman',
      company: 'DolarApp',
      img: '/people/zach_garman.jpeg',
      companyImg: '/logo/dolarapp.png',
      description: (
        <p>
          Brew&apos;s AI helps us turn complex product updates into clear,
          engaging emails.
          <Highlight>Our click-through rates have never been better.</Highlight>{' '}
          It&apos;s like having a copywriter who perfectly understands our
          product.
        </p>
      ),
    },
    {
      id: '9',
      name: 'Dan Westgarth',
      company: 'Deel',
      img: '/people/dan_westgarth.jpeg',
      companyImg: '/logo/deel.png',
      description: (
        <p>
          From campaign generation to performance analysis, Brew has streamlined
          our entire email marketing workflow.
          <Highlight>It&apos;s an indispensable tool.</Highlight> We&apos;ve
          10x&apos;d our email output without adding headcount.
        </p>
      ),
    },
  ],
  faqSection: {
    title: 'Still have questions?',
    description: (
      <>
        We&apos;re here to help! Feel free to{' '}
        <a
          href={BREW_DEMO_BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-primary hover:underline"
        >
          schedule a call
        </a>
        .
      </>
    ),
    pricingFaQitems: [
      {
        id: 'ai-credits',
        question: 'What are AI credits?',
        answer: [
          `AI credits pay for what Brew's AI does for you: generating and editing emails, creating images, writing copy, and analyzing performance. One credit is ${formatCurrency({ amount: USD_PER_CREDIT, currency: 'USD' })} of model spend, and an email costs what the model actually used, so a long email or a bigger model costs more than a short one. Importing an email (HTML, a Figma frame, or a forwarded email) and exporting one to another tool are free; recreating an email from a screenshot uses credits, because Brew's AI rebuilds it. Credits reset every month and do not roll over.`,
        ],
      },
      {
        id: 'run-out-credits',
        question: 'What happens if I run out of credits?',
        answer: [
          'AI features pause until your credits reset at the start of your next billing cycle, or until you upgrade. Sending has its own monthly allowance, so you can still send the emails you have. If you run out every month, the next plan up is the fix.',
        ],
      },
      {
        id: 'change-plans',
        question: 'Can I change plans at any time?',
        answer: [
          "Yes. You can upgrade, downgrade, or cancel at any time. When you upgrade, you get immediate access to your new plan's features. When you downgrade, the change takes effect at the end of your current billing period.",
        ],
      },
      {
        id: 'sending-domain',
        question: "What's a sending domain?",
        answer: [
          'A sending domain is the domain your emails are sent from (e.g., hello@yourbrand.com). Live campaign and automation sends require a verified custom domain you own. The Brew default sender is reserved for test sends.',
        ],
      },
      {
        id: 'try-before-commit',
        question: 'Can I try Brew before committing?',
        answer: [
          "We encourage you to start with our free plan, no credit card required. It's the perfect way to experience the full power of Brew and see firsthand how it can transform your email marketing workflow. When you're ready to unlock even more potential and scale your efforts, upgrading is seamless with our transparent, growth-friendly pricing.",
        ],
      },
      {
        id: 'team-members-charge',
        question: 'Do you charge for team members?',
        answer: [
          'No. All Brew plans include unlimited team seats at no additional cost. We believe collaboration is essential for effective email marketing.',
        ],
      },
      {
        id: 'support',
        question: 'How do I get support?',
        answer: [
          'Directly from our team: the chat inside Brew, email at support@brew.new, the Brew Slack community, and the docs at docs.brew.new. We also get on calls to help with domain setup, imports, and your first emails.',
        ],
      },
      {
        id: 'enterprise-included',
        question: "What's included in Enterprise?",
        answer: [
          "Custom AI credit and email send volumes, unlimited brands, and a dedicated account manager. Reach out and we'll build a plan around your needs.",
        ],
      },
    ],
    mcpFaQitems: [
      {
        id: 'mcp-what-is',
        question: 'What is the Brew MCP server?',
        answer: [
          'It is a Model Context Protocol (MCP) server that exposes Brew to AI agents. Connect it in ChatGPT, Claude, Grok, Cursor, Codex, or any MCP client and your agent can generate and iterate on email designs, send and schedule campaigns, build automations, manage contacts and segments, and read performance analytics, using the same brand and data as the Brew app.',
        ],
      },
      {
        id: 'mcp-how-connect',
        question: 'How do I connect it?',
        answer: [
          'In ChatGPT, enable Developer mode under Settings → Security and login, then create Brew under Settings → Plugins with OAuth and https://brew.new/api/mcp. In Claude, Grok, Codex, Cursor, or another MCP client, add the same server URL and complete Brew sign-in. Then call get_brew_capabilities to verify the catalog. If the client does not support OAuth, use a brand-scoped Brew API key from Settings → API.',
        ],
      },
      {
        id: 'mcp-compose-crm',
        question: 'Can I combine it with other MCPs like Clay or Attio?',
        answer: [
          'Yes. That is where it shines. Brew is the AI-native ESP: the email memory and sending infrastructure your agent operates. Pair it with a CRM or enrichment MCP (Clay, Attio, and others) to add rich contact context, build intelligent segments, and send highly personalized email. Your agent composes them together.',
        ],
      },
      {
        id: 'mcp-vs-app',
        question: 'Is it as powerful as the app?',
        answer: [
          'It covers the full email marketing cycle: create, iterate, send, schedule, automate, segment, and analyze. Anything you do in the in-app chat you can do from an agent, and agents can also run automations directly. The one in-app-only feature is send-time optimization.',
        ],
      },
    ],
    generalFaQitems: [
      {
        id: 'differentiation',
        question: 'How is Brew different from other email marketing platforms?',
        answer: [
          'Traditional ESPs give you software but leave you to figure out strategy, copywriting, design, and analysis on your own. This makes email marketing slow, expensive, and frustrating. Brew is the AI-native ESP: your brand lives in markdown files Brew reads at generation time, so every send ships on-brand. Our self-improving AI system:',
          'Generates on-brand emails in seconds',
          'Creates entire automation sequences with a single prompt',
          'Learns what works for your specific audience',
          'Provides actionable insights through natural language queries',
          'Continuously improves your results over time',
          'You set the creative direction; Brew does the work. This makes email your highest-ROI marketing channel without the traditional complexity and resource requirements.',
        ],
        isList: false,
        isListPoints: [
          'Generates on-brand emails in seconds',
          'Creates entire automation sequences with a single prompt',
          'Learns what works for your specific audience',
          'Provides actionable insights through natural language queries',
          'Continuously improves your results over time',
        ],
      },
      {
        id: 'can-send-emails',
        question: 'Can I send emails with Brew?',
        answer: [
          'Absolutely! Whether you need to send one-off marketing campaigns, regular newsletters, sophisticated automation sequences, or essential transactional emails like order confirmations, Brew has you covered.',
        ],
      },
      {
        id: 'cold-outreach',
        question: 'Can I use Brew for cold outreach?',
        answer: [
          "No, Brew is not for cold outreach. Brew helps you engage customers who've consented to receive emails and who expect to receive emails from you. Maybe we'll launch 'Cold Brew' at some point in the future.",
        ],
      },
      {
        id: 'email-deliverability',
        question: 'How does Brew handle email deliverability?',
        answer: [
          'Brew takes email deliverability seriously with several built-in features:',
          'Automatic domain authentication (DKIM, SPF, and DMARC)',
          'Domain warming recommendations and guidance',
          'Intelligent sending algorithms that optimize delivery timing',
          'List hygiene guides to maintain a healthy subscriber database',
          'Deliverability monitoring and alerts',
          'Compliance with global email regulations',
          'Our system is designed to ensure your emails reach the inbox, not the spam folder.',
        ],
        isList: false,
        isListPoints: [
          'Automatic domain authentication (DKIM, SPF, and DMARC)',
          'Domain warming recommendations and guidance',
          'Intelligent sending algorithms that optimize delivery timing',
          'List hygiene guides to maintain a healthy subscriber database',
          'Deliverability monitoring and alerts',
          'Compliance with global email regulations',
        ],
      },
      {
        id: 'automation-types',
        question: 'What types of automations can I create with Brew?',
        answer: [
          'Brew enables you to create a wide range of powerful automations to engage your audience at every stage of their journey:',
          'Welcome sequences that introduce new subscribers to your brand',
          'Onboarding flows that help users get the most from your product',
          'Re-engagement campaigns for inactive subscribers',
          'Birthday and anniversary emails',
          'Abandoned cart reminders',
          'Post-purchase follow-ups',
          'Subscription renewal notifications',
          'Event-triggered emails based on user behavior',
          'And many more',
          'Our AI can help you design these automations with just a few prompts, making it easy to create sophisticated email sequences that would typically require significant time and expertise.',
        ],
        isList: false,
        isListPoints: [
          'Welcome sequences that introduce new subscribers to your brand',
          'Onboarding flows that help users get the most from your product',
          'Re-engagement campaigns for inactive subscribers',
          'Birthday and anniversary emails',
          'Abandoned cart reminders',
          'Post-purchase follow-ups',
          'Subscription renewal notifications',
          'Event-triggered emails based on user behavior',
          'And many more',
        ],
      },
      {
        id: 'ai-email-creation',
        question: "How does Brew's AI help with email creation?",
        answer: [
          "Brew's AI handles the heavy-lifting of the email creation process for you. Simply provide a brief prompt about what you want to communicate, and our AI will:",
          'Write compelling copy that matches your brand voice',
          'Design visually appealing layouts that align with your brand guidelines',
          'Personalize content based on subscriber data',
          'Optimize subject lines for better open rates',
          'You maintain full control to review and make adjustments before sending, but the heavy lifting is done for you.',
        ],
        isList: false,
        isListPoints: [
          'Write compelling copy that matches your brand voice',
          'Design visually appealing layouts that align with your brand guidelines',
          'Personalize content based on subscriber data',
          'Optimize subject lines for better open rates',
        ],
      },
      {
        id: 'custom-sending-domain',
        question: 'Can I use my own sending domain with Brew?',
        answer: [
          'Yes! Using your own sending domain is necessary and easy to set up in Brew:',
          'Add your domain in the Brew settings',
          'Follow our step-by-step instructions to add the required DNS records',
          'Brew will verify your domain automatically once the records propagate',
          'Using your own domain improves deliverability, builds brand trust, and ensures your emails are recognized by recipients. We provide full support for DKIM, SPF, and DMARC authentication to maximize inbox placement.',
        ],
        isList: false,
        isListPoints: [
          'Add your domain in the Brew settings',
          'Follow our step-by-step instructions to add the required DNS records',
          'Brew will verify your domain automatically once the records propagate',
        ],
      },
    ],
  },
  ctaSection: {
    id: 'cta',
    title: 'Brew something special.',
    button: {
      text: 'Get Started for Free',
      href: '/signup',
    },
    subtext: 'No credit card required.',
  },
  footerLinks: [
    {
      title: 'Company',
      links: [
        { id: 1, title: 'About', url: '#' },
        { id: 2, title: 'Contact', url: '#' },
        { id: 3, title: 'Blog', url: '#' },
        { id: 4, title: 'Story', url: '#' },
      ],
    },
    {
      title: 'Resources',
      links: [
        { id: 9, title: 'Documentation', url: '#' },
        { id: 10, title: 'Careers', url: '#' },
        { id: 11, title: 'Newsletters', url: '#' },
        { id: 12, title: 'More', url: '#' },
      ],
    },
  ],
}
